import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { calculateBudgetImpact } from '@/utils/supreme-intelligence/budgetCalculations';
import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { AlertCircle, TrendingDown, TrendingUp, Info } from 'lucide-react';

interface BudgetImpactAnalyzerProps {
  formData: EnhancedUnifiedFormData;
  mortgagePayment: number;
}

export const BudgetImpactAnalyzer: React.FC<BudgetImpactAnalyzerProps> = ({
  formData,
  mortgagePayment
}) => {
  // Check if we have real income data (not default values)
  const hasIncomeData = formData.primaryIncome.annualIncome > 0 && 
                        formData.primaryIncome.annualIncome !== 75000;

  // If income data is missing, show informative message
  if (!hasIncomeData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Budget Impact Analyzer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <Info className="w-12 h-12 mx-auto text-muted-foreground" />
            <div>
              <p className="text-muted-foreground mb-2">
                The Budget Impact Analyzer requires income and debt information.
              </p>
              <p className="text-sm text-muted-foreground">
                Use the <strong>Unified Calculator</strong> to access this feature with complete financial analysis.
              </p>
            </div>
            <Button onClick={() => window.location.href = '/unified-calculator'}>
              Go to Unified Calculator
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const budget = calculateBudgetImpact(formData, mortgagePayment);

  const chartData = budget.categories.map(cat => ({
    name: cat.name,
    value: cat.withMortgageAmount
  }));

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--destructive))', 'hsl(var(--secondary))', 
    'hsl(var(--accent))', 'hsl(var(--muted))', 'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 
    'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 text-foreground">Budget Impact Analysis</h3>
        
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="font-medium mb-3 text-foreground">Monthly Budget Breakdown</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="hsl(var(--primary))"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${Math.round(value as number).toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2 text-foreground">Key Metrics</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monthly Income:</span>
                  <span className="font-medium text-foreground">${budget.monthlyIncome.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mortgage Payment:</span>
                  <span className="font-medium text-foreground">${budget.mortgagePayment.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Monthly Debt:</span>
                  <span className="font-medium text-foreground">${budget.totalMonthlyDebt.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <span className="text-muted-foreground">Disposable Income:</span>
                  <div className="flex items-center gap-2">
                    {budget.disposableIncomeAfter < budget.disposableIncomeBefore ? (
                      <TrendingDown className="h-4 w-4 text-destructive" />
                    ) : (
                      <TrendingUp className="h-4 w-4 text-primary" />
                    )}
                    <span className="font-bold text-foreground">${budget.disposableIncomeAfter.toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Savings Rate:</span>
                  <span className="font-medium text-foreground">{budget.savingsRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Emergency Fund:</span>
                  <span className="font-medium text-foreground">{budget.emergencyFundMonths.toFixed(1)} months</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="font-medium text-foreground">Category Details</h4>
          {budget.categories.map((cat, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{cat.name}</span>
                  {cat.adjustable && (
                    <span className="text-xs px-2 py-0.5 rounded bg-accent text-accent-foreground">Adjustable</span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {cat.percentage.toFixed(1)}% of income
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium text-foreground">${cat.withMortgageAmount.toLocaleString()}/mo</div>
                {cat.currentAmount !== cat.withMortgageAmount && (
                  <div className="text-sm text-muted-foreground">
                    was ${cat.currentAmount.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {budget.recommendations.length > 0 && (
          <div className="mt-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-2">Recommendations</h4>
                <ul className="space-y-1">
                  {budget.recommendations.map((rec, idx) => (
                    <li key={idx} className="text-sm text-amber-800 dark:text-amber-200">• {rec}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
